import React, { useEffect } from 'react';
import Icon from '@mdi/react';
import {
  mdiMagnify,
  mdiCalendarWeek,
  mdiLinkVariant,
  mdiClockOutline,
  mdiShape,
  mdiFingerprint,
  mdiAccountAlert,
  mdiIpNetwork,
  mdiWeb,
  mdiShopping,
} from '@mdi/js';
import { Card, Avatar, Tooltip, Typography, Spin, Divider, Flex } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import useQueryParams from 'hooks/useQueryParams';
import getFormattedNumber from 'store/redux/utils/getFormattedNumber';
import ninjaApi from 'store/redux/apis';
import {
  AdFormatter,
  AdsetFormatter,
  CampaignFormatter,
  UrlFormatter,
} from 'components/tables/NinjaTable/formatters/sourceFormatters';
import JourneyUrlFormatter from 'modules/ReportModule/components/JourneyUrlFormatter';
import UserReports from './UserReports';
import EventTypeIcons from 'components/icons/EventTypeIcons';

const { Text, Paragraph } = Typography;

const CountCard = ({ label, value }) => (
  <Card className='sm:flex-1 w-full !h-fit rounded-xl shadow-sm text-center border border-solid border-background-light'>
    <Text className='text-xs font-medium'>{label}</Text>
    <div className='!m-0 !text-lg font-semibold'>{getFormattedNumber(value)}</div>
  </Card>
);

const GridItem = ({ icon, label, children, span = 1 }) => (
  <div
    className={`items-center flex gap-1 border border-background-light rounded-lg ${
      span > 1 ? `col-span-${span}` : ''
    }`}
  >
    <div className='flex items-center gap-1 font-normal text-sm'>
      {icon && <Icon path={icon} size={0.6} className='text-gray' />}
      <span className='text-gray'>{label}</span>:
    </div>
    <div className='!text-sm font-semibold w-full'>{children || '-'}</div>
  </div>
);

const EventDetailsPanel = () => {
  const [getEventDetails, { data, isLoading }] = ninjaApi.useLazyGetEventDetailsQuery();
  const { searchParams } = useQueryParams();
  const { event_details_id, session_details_id } = searchParams;

  useEffect(() => {
    if (event_details_id) getEventDetails({ event_id: event_details_id }).unwrap();
    else if (session_details_id) getEventDetails({ session_id: session_details_id }).unwrap();
  }, [event_details_id, session_details_id]);

  if (isLoading) {
    return (
      <div className='flex justify-center items-center h-full min-h-[200px]'>
        <Spin indicator={<LoadingOutlined spin />} size='large' className='mt-5' />
      </div>
    );
  }

  const events = data?.data?.events?.[0];
  const user = data?.data?.user;
  const session = data?.data;
  const event_type_counts = data?.meta?.event_type_counts || {};

  const eventItems = events
    ? [
        {
          icon: mdiShape,
          label: 'Type',
          children: (
            <EventTypeIcons
              event={events.type?.type}
              showIcon={false}
              showEventName={true}
              className='text-xs'
            />
          ),
        },
        { icon: mdiFingerprint, label: 'ID', children: <Text copyable>{events.id}</Text> },
        { icon: mdiClockOutline, label: 'Date', children: events.date },
        events?.referrer?.page && {
          icon: mdiLinkVariant,
          label: 'Referrer',
          children: events?.referrer?.page ? (
            <JourneyUrlFormatter url={events?.referrer?.page} />
          ) : (
            '-'
          ),
        },
        events?.shopify_event?.product?.id && {
          icon: mdiShopping,
          label: 'Product',
          span: 4,
          children: (
            <Tooltip
              placement='bottomLeft'
              title={
                <img
                  src={events?.shopify_event?.product?.media_file?.url}
                  alt='Product'
                  className='!object-contain rounded-md w-24 h-24'
                />
              }
            >
              <div className='flex items-center gap-2'>
                <Avatar
                  src={events?.shopify_event?.product?.media_file?.url}
                  alt='Product'
                  className='object-cover rounded-sm w-6 h-6'
                />
                <Paragraph ellipsis={{ rows: 1, expandable: true }} className='!m-0 text-sm'>
                  {events?.shopify_event?.product?.name}
                </Paragraph>
              </div>
            </Tooltip>
          ),
        },
      ].filter(Boolean)
    : [];

  const sessionItems = session
    ? [
        ...Object.entries(event_type_counts).map(([key, value]) => ({
          label: (
            <div className='w-max text-sm'>
              {key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())}
            </div>
          ),
          children: getFormattedNumber(value),
        })),
        { icon: mdiIpNetwork, label: 'Address', children: session?.ip?.ip },
        session?.website?.domain && {
          icon: mdiWeb,
          label: 'Domain',
          children: session?.website?.domain ? (
            <JourneyUrlFormatter url={session.website.domain} />
          ) : (
            '-'
          ),
        },
        session?.ninja_url && {
          label: 'URL',
          span: 2,

          children: <UrlFormatter row={{ url: session.ninja_url }} />,
        },
        session?.ninja_url && {
          label: 'Ad',
          span: 2,
          children: <AdFormatter row={session.ninja_url} disableJourneyFilter={false} />,
        },
        session?.ninja_url?.ad && {
          label: 'Adset',
          span: 2,

          children: <AdsetFormatter row={session.ninja_url.ad} />,
        },
        session?.ninja_url?.ad?.adset && {
          label: 'Campaign',
          span: 2,

          children: <CampaignFormatter row={session.ninja_url.ad.adset} />,
        },
      ].filter(Boolean)
    : [];

  const renderSection = (title, icon, itemsArray, countsArray) =>
    itemsArray.length ? (
      <div className='grid gap-4'>
        <Divider orientation='center'>
          <Flex align='center' className='text-sm font-medium' gap='small'>
            <span>{title}</span>
            <Icon path={icon} size={0.6} />
          </Flex>
        </Divider>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3'>
          {itemsArray.map((item, idx) => (
            <GridItem key={idx} {...item} />
          ))}
        </div>
        <div className='flex gap-4 flex-wrap mt-2'>
          {countsArray.map(({ label, value }) => (
            <CountCard key={label} label={label} value={value} />
          ))}
        </div>
      </div>
    ) : null;

  return (
    <div className='shadow-md overflow-y-auto overflow-x-hidden w-full h-full p-4 flex flex-col gap-4'>
      <UserReports sendConversion={session?.type?.type !== 'pageview'} userData={user} />
      <div className='flex gap-4 flex-wrap'>
        <CountCard label='Leads Count' value={user?.leads_count} />
        <CountCard label='Postbacks Count' value={user?.postbacks_count} />
        <CountCard label='Deals Count' value={user?.deals_count} />
      </div>

      {!data?.data && (
        <div className='flex flex-col items-center justify-center h-full gap-2 min-h-[200px]'>
          <Icon
            path={mdiMagnify}
            size={1.4}
            className='p-1 rounded-md text-color-xlight bg-background-light'
          />
          <Typography className='text-xs font-normal'>
            {!event_details_id ? 'Select an event to view its properties' : 'No Data Found'}
          </Typography>
        </div>
      )}

      {renderSection('Events', mdiCalendarWeek, eventItems, [
        { label: 'Leads Count', value: events?.leads_count },
        { label: 'Postbacks Count', value: events?.postbacks_count },
        { label: 'Deals Count', value: events?.deals_count },
      ])}

      {renderSection('Session', mdiAccountAlert, sessionItems, [
        { label: 'Leads Count', value: session?.leads_count },
        { label: 'Postbacks Count', value: session?.postbacks_count },
        { label: 'Deals Count', value: session?.deals_count },
      ])}
    </div>
  );
};

export default EventDetailsPanel;
